import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { CustomSwalService } from "../../app/helpers/custom-swal.service";
import { TranslateService } from "@ngx-translate/core";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class AutoSaveLocalStorageService {
  private _unsubscribeAll: Subject<any>;

  constructor(
    private readonly router: Router,
    private readonly customSwalService: CustomSwalService,
    private readonly _translateService: TranslateService
  ) {
    this._unsubscribeAll = new Subject();
  }

  /**
   * Guardar datos en localStorage
   * @param key - Clave para almacenar los datos
   * @param data - Datos a guardar
   */
  saveData(key: string, data: any): void {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      console.log(`✅ Datos guardados automáticamente con clave: ${key}`);
    } catch (error) {
      console.error(
        `❌ Error al guardar en localStorage con clave ${key}:`,
        error
      );
    }
  }

  /**
   * Cargar datos desde localStorage
   * @param key - Clave de los datos a cargar
   * @returns Los datos parseados o null si no existen
   */
  loadData<T>(key: string): T | null {
    try {
      const savedData = localStorage.getItem(key);
      if (savedData) {
        console.log(`✅ Datos cargados desde localStorage con clave: ${key}`);
        return JSON.parse(savedData) as T;
      }
      return null;
    } catch (error) {
      console.error(
        `❌ Error al cargar datos del localStorage con clave ${key}:`,
        error
      );
      return null;
    }
  }

  /**
   * Eliminar datos del localStorage
   * @param key - Clave de los datos a eliminar
   */
  clearData(key: string): void {
    try {
      localStorage.removeItem(key);
      console.log(`🗑️ Datos eliminados del localStorage con clave: ${key}`);
    } catch (error) {
      console.error(
        `❌ Error al eliminar datos del localStorage con clave ${key}:`,
        error
      );
    }
  }

  /**
   * Verificar si existe una clave en localStorage
   * @param key - Clave a verificar
   * @returns true si existe, false si no
   */
  hasData(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Guardar estado de autoguardado (habilitado/deshabilitado)
   * @param key - Clave para el estado de autoguardado
   * @param enabled - Estado habilitado/deshabilitado
   */
  saveAutosaveState(key: string, enabled: boolean): void {
    try {
      localStorage.setItem(key, JSON.stringify(enabled));
      console.log(
        `💾 Estado de autoguardado ${
          enabled ? "habilitado" : "deshabilitado"
        } guardado con clave: ${key}`
      );
    } catch (error) {
      console.error(
        `❌ Error al guardar estado de autoguardado con clave ${key}:`,
        error
      );
    }
  }

  /**
   * Cargar estado de autoguardado
   * @param key - Clave del estado de autoguardado
   * @param defaultValue - Valor por defecto si no existe (default: true)
   * @returns Estado de autoguardado
   */
  loadAutosaveState(key: string, defaultValue: boolean = true): boolean {
    try {
      const savedState = localStorage.getItem(key);
      return savedState !== null ? JSON.parse(savedState) : defaultValue;
    } catch (error) {
      console.error(
        `❌ Error al cargar estado de autoguardado con clave ${key}:`,
        error
      );
      return defaultValue;
    }
  }

  /**
   * Autoguardar datos solo si el autoguardado está habilitado
   * @param dataKey - Clave para los datos
   * @param stateKey - Clave para el estado de autoguardado
   * @param data - Datos a guardar
   */
  autoSaveIfEnabled(dataKey: string, stateKey: string, data: any): void {
    const isEnabled = this.loadAutosaveState(stateKey);

    if (isEnabled) {
      this.saveData(dataKey, data);
    } else {
      console.log(
        `🚫 Autoguardado deshabilitado para clave: ${dataKey} - no se guardó`
      );
    }
  }

  /**
   * Limpiar todos los datos relacionados con una funcionalidad
   * @param keys - Array de claves a eliminar
   */
  clearMultipleKeys(keys: string[]): void {
    keys.forEach((key) => this.clearData(key));
  }

  /**
   * Verificar si hay datos guardados y si viene de navegación nueva
   * @param dataKey - Clave de los datos
   * @param isNewForm - Indica si viene del botón "nuevo formulario"
   * @returns true si debe preguntar al usuario, false si debe continuar automáticamente
   */
  shouldAskUserToContinue(
    dataKey: string,
    isNewForm: boolean = false
  ): boolean {
    // Si viene de recarga de página, siempre continuar
    if (!isNewForm) {
      console.log(`📄 Recarga de página detectada, continuando automáticamente`);
      return false;
    }

    // Si viene del botón nuevo y hay datos guardados, preguntar
    const hasData = this.hasData(dataKey);
    console.log(`🔍 Verificando datos guardados para ${dataKey}: ${hasData ? 'SÍ' : 'NO'}`);
    return hasData;
  }

  /**
   * Quitar el parámetro new=true de la URL sin recargar la página
   */
  private removeNewParamFromUrl(route: any): void {
    const urlTree = this.router.createUrlTree([], {
      relativeTo: route,
      queryParams: { new: null },
      queryParamsHandling: "merge",
    });
    this.router.navigateByUrl(urlTree, { replaceUrl: true });
    console.log(`🔗 Parámetro 'new' removido de la URL`);
  }

  /**
   * Quitar el parámetro new=true de la URL y recargar la página
   */
  private removeNewParamAndReload(route: any): void {
    // Construir la URL sin el parámetro new
    const urlTree = this.router.createUrlTree([], {
      relativeTo: route,
      queryParams: { new: null },
      queryParamsHandling: "merge",
    });

    // Navegar a la nueva URL y luego recargar
    this.router.navigateByUrl(urlTree, { replaceUrl: true }).then(() => {
      console.log(`🔄 Recargando página después de remover parámetro 'new'`);
      window.location.reload();
    });
  }

  /**
   * Manejar todo el flujo completo de autoguardado: procesar parámetros URL, mostrar alertas, cargar datos
   * @param dataKey - Clave para los datos
   * @param stateKey - Clave para el estado de autoguardado
   * @param route - ActivatedRoute para manejar parámetros URL
   * @returns Promise con los datos cargados o null
   */
  async handleAutosaveFlow<T>(
    dataKey: string,
    stateKey: string,
    route: any
  ): Promise<T | null> {
    console.log(`🚀 Iniciando flujo de autoguardado para ${dataKey}`);
    
    // Cargar preferencia de autoguardado (por defecto true)
    const autosaveEnabled = this.loadAutosaveState(stateKey, true);
    console.log(`⚙️ Autoguardado habilitado: ${autosaveEnabled}`);

    // Detectar si viene del botón "nuevo formulario" a través de query params
    const isNewForm = route.snapshot.queryParams["new"] === "true";
    const isEditForm = route.snapshot.queryParams["new"] === "false";

    console.log(`📋 Parámetros URL: new=${route.snapshot.queryParams["new"]} (isNewForm: ${isNewForm}, isEditForm: ${isEditForm})`);

    // Verificar si debe preguntar al usuario
    if (this.shouldAskUserToContinue(dataKey, isNewForm)) {
      console.log(`❓ Preguntando al usuario si desea reanudar el proceso...`);
      
      try {
        // Convertir el observable en una Promise
        const translations = await this._translateService
          .get([
            "AUTOSAVE.QUESTION.TITLE",
            "AUTOSAVE.QUESTION.MESSAGE",
            "AUTOSAVE.QUESTION.CONFIRMTEXT",
            "AUTOSAVE.QUESTION.CANCELTEXT",
          ])
          .pipe(takeUntil(this._unsubscribeAll))
          .toPromise();

        const result = await this.customSwalService.showQuestion(
          translations["AUTOSAVE.QUESTION.TITLE"],
          translations["AUTOSAVE.QUESTION.MESSAGE"],
          translations["AUTOSAVE.QUESTION.CONFIRMTEXT"],
          translations["AUTOSAVE.QUESTION.CANCELTEXT"]
        );

        if (result.isConfirmed) {
          // Usuario quiere continuar con el proceso anterior
          console.log(`✅ Usuario eligió reanudar el proceso anterior`);
          this.removeNewParamFromUrl(route);
          return this.loadDataIfEnabled<T>(dataKey, autosaveEnabled);
        } else {
          // Usuario quiere empezar nuevo, limpiar datos guardados
          console.log(`🆕 Usuario eligió empezar nuevo proceso`);
          this.clearData(dataKey);
          // Quitar el parámetro new=true y recargar la página
          this.removeNewParamAndReload(route);
          return null;
        }
      } catch (error) {
        console.error(`❌ Error en el flujo de autoguardado:`, error);
        // En caso de error, continuar con datos guardados si existen
        this.removeNewParamFromUrl(route);
        return this.loadDataIfEnabled<T>(dataKey, autosaveEnabled);
      }
    } else if (isEditForm) {
      // Si es edición, solo remover el parámetro new
      console.log(`✏️ Modo edición, removiendo parámetro 'new'`);
      this.removeNewParamFromUrl(route);
      return null; // En edición no cargar autoguardado
    } else {
      // Cargar automáticamente si no viene del botón nuevo o no hay datos guardados
      // Si viene con el parámetro new=true pero no hay datos guardados, quitarlo
      if (isNewForm) {
        console.log(`🆕 Formulario nuevo sin datos guardados, removiendo parámetro 'new'`);
        this.removeNewParamFromUrl(route);
      }
      
      console.log(`📂 Cargando datos automáticamente...`);
      return this.loadDataIfEnabled<T>(dataKey, autosaveEnabled);
    }
  }

  /**
   * Cargar datos solo si el autoguardado está habilitado
   * @param dataKey - Clave de los datos
   * @param autosaveEnabled - Estado del autoguardado
   * @returns Los datos cargados o null
   */
  private loadDataIfEnabled<T>(
    dataKey: string,
    autosaveEnabled: boolean
  ): T | null {
    if (autosaveEnabled) {
      const savedData = this.loadData<T>(dataKey);
      if (savedData) {
        console.log(`📊 Datos restaurados exitosamente desde ${dataKey}`);
        return savedData;
      } else {
        console.log(`📭 No hay datos guardados en ${dataKey}`);
      }
    } else {
      console.log(`🚫 Autoguardado deshabilitado, no se cargan datos`);
    }
    return null;
  }

  /**
   * Manejar toggle del autoguardado
   * @param dataKey - Clave para los datos
   * @param stateKey - Clave para el estado de autoguardado
   * @param enabled - Estado del autoguardado
   * @param currentData - Datos actuales para guardar si se habilita
   */
  handleAutosaveToggle(
    dataKey: string,
    stateKey: string,
    enabled: boolean,
    currentData: any
  ): void {
    // Guardar preferencia
    this.saveAutosaveState(stateKey, enabled);

    if (!enabled) {
      // Si se desactiva, limpiar datos guardados
      this.clearData(dataKey);
    } else {
      // Si se activa, guardar datos actuales
      this.saveData(dataKey, currentData);
    }
  }

  /**
   * Limpiar autoguardado al salir o cancelar
   * @param dataKey - Clave de los datos a limpiar
   */
  clearAutosaveOnExit(dataKey: string): void {
    this.clearData(dataKey);
  }

  /**
   * Navegar a una ruta con parámetro new=true para indicar formulario nuevo
   * @param route - Ruta a navegar
   */
  navigateToNewForm(route: string): void {
    console.log(`🚀 Navegando a formulario nuevo: ${route}?new=true`);
    this.router.navigate([route], { queryParams: { new: "true" } });
  }

  /**
   * Navegar a una ruta con parámetro new=false para indicar edición
   * @param route - Ruta a navegar
   */
  navigateToEditForm(route: string): void {
    console.log(`✏️ Navegando a formulario de edición: ${route}?new=false`);
    this.router.navigate([route], { queryParams: { new: "false" } });
  }

  /**
   * Verificar si existe data de autoguardado sin mostrar modal
   * @param key Clave del localStorage
   * @returns true si existe data guardada, false si no
   */
  hasAutosaveData(key: string): boolean {
    const savedData = localStorage.getItem(key);
    if (!savedData) {
      return false;
    }

    try {
      const parsedData = JSON.parse(savedData);
      // Verificar que el objeto no esté vacío
      const hasData = parsedData && Object.keys(parsedData).length > 0;
      console.log(`🔍 Verificación de autoguardado para ${key}: ${hasData ? 'TIENE DATOS' : 'VACÍO'}`);
      return hasData;
    } catch (error) {
      console.error("❌ Error al verificar autoguardado:", error);
      return false;
    }
  }
}